/**
 * Hộp "Danh sách thiết bị kết nối" — mở từ nút "Thêm mới" của trang quản trị cụm
 * loa / bảng điện tử. Liệt kê thiết bị CHƯA ĐĂNG KÝ đã gửi DV008 lên (trạng thái
 * `phat_hien`); quản trị tích chọn rồi "Đăng ký" để đưa vào kho chờ đăng ký.
 * Lọc, đếm, phân trang ở phía máy chủ.
 */
import { useEffect, useId, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { dienVao } from '../ngon-ngu/vi';
import { viThietBi } from '../ngon-ngu/vi-thiet-bi';
import {
  khoThietBiApi,
  LoiApi,
  type DongKho,
  type KhoThietBiApi,
  type LoaiThietBiKho,
} from '../api/kho-thiet-bi-api';
import type { NutDiaBan, TrangDuLieu } from '../api/thiet-bi-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { BangDuLieu, SO_DONG_MAC_DINH, type CotBang } from '../thanh-phan/BangDuLieu';
import { TheTag } from '../thanh-phan/MauGiaoDien';
import { ChonDonViHanhChinh } from './MH02DanhSachThietBi';

const t = viThietBi.kho;

export const thoiDiemNgan = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('vi-VN', { hour12: false });
};

/** Cột "Kết nối": trạng thái mạng thiết bị tự báo + hoạt động/không. */
/**
 * TrangThaiKetNoi của DV008 (Công văn 276): "3G/4G/5G kèm cường độ", "Ethernet", "Wifi"
 * → Đang kết nối; "Mất kết nối" hoặc rỗng → Mất kết nối.
 */
export function dangKetNoi(trangThaiKetNoi: string | null): boolean {
  const s = (trangThaiKetNoi ?? '').normalize('NFC').trim().toLowerCase();
  return s !== '' && s !== 'mất kết nối' && s !== 'mat ket noi';
}

export function OKetNoi({ d }: { d: DongKho }): ReactNode {
  const coKetNoi = dangKetNoi(d.trangThaiKetNoi);
  return (
    <>
      <TheTag kieu={coKetNoi ? 'ok' : 'do'}>{coKetNoi ? t.dangKetNoi : t.matKetNoi}</TheTag>
      {coKetNoi ? <span className="bang__dong-phu">{d.trangThaiKetNoi}</span> : null}
    </>
  );
}

/** Cột "Phương thức kết nối": thiết bị MQTT tự kích hoạt (trực tiếp) hay qua bộ tương thích. */
export function OPhuongThuc({ d }: { d: DongKho }): ReactNode {
  const trucTiep = d.phuongThucKetNoi === 'truc_tiep';
  return (
    <TheTag kieu={trucTiep ? 'xanh' : 'trung-tinh'}>
      {trucTiep ? t.phuongThuc.truc_tiep : t.phuongThuc.gian_tiep}
    </TheTag>
  );
}

/** Thiết bị trực tiếp mà địa bàn chỉ là mã tỉnh (hoặc trống): đăng ký được vào xã bất kỳ đang chọn. */
export function laChuaCoXa(d: Pick<DongKho, 'phuongThucKetNoi' | 'nguonId'>): boolean {
  return d.phuongThucKetNoi === 'truc_tiep' && !(d.nguonId ?? '').includes('.');
}

/** Tên xã theo danh mục; mã lạ thì ghi rõ để quản trị biết phải chọn địa bàn. */
export function ODiaBan({ d }: { d: DongKho }): ReactNode {
  if (d.tenXa) return <>{d.tenXa}</>;
  // Thiết bị trực tiếp chờ đăng ký chưa biết xã: DV008 mang mã tỉnh, quản trị chọn xã khi đăng ký.
  if (laChuaCoXa(d)) {
    return (
      <>
        {t.chuaXacDinhXa}
        <span className="bang__dong-phu">
          <TheTag kieu="xanh">{t.chonXaKhiDangKy}</TheTag>
        </span>
      </>
    );
  }
  return (
    <>
      {d.tenNguon ?? d.nguonId ?? '—'}
      <span className="bang__dong-phu">
        <TheTag kieu="vang">{t.ngoaiDanhMuc}</TheTag>
      </span>
    </>
  );
}

export function HopThietBiKetNoi({
  loai,
  cay,
  api = khoThietBiApi,
  onDong,
  onDaDuaVaoKho,
}: {
  loai: LoaiThietBiKho;
  cay: NutDiaBan[];
  api?: KhoThietBiApi;
  onDong: () => void;
  onDaDuaVaoKho: (so: number) => void;
}): ReactNode {
  const idTieuDe = useId();
  const [nguon, datNguon] = useState('');
  const [trang, datTrang] = useState(1);
  const [soDong, datSoDong] = useState(SO_DONG_MAC_DINH);
  const [daChon, datDaChon] = useState<Set<string>>(new Set());
  const [dangGui, datDangGui] = useState(false);
  const [loi, datLoi] = useState<string | null>(null);

  useEffect(() => {
    const esc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onDong();
    };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onDong]);

  const kq = dungTaiDuLieu<TrangDuLieu<DongKho>>({
    tai: (signal) =>
      api.danhSach(
        {
          trangThai: 'phat_hien',
          loaiThietBi: loai,
          nguonId: nguon || undefined,
          page: trang,
          pageSize: soDong,
        },
        signal,
      ),
    laRong: (d) => d.items.length === 0,
    dangLoc: nguon !== '',
    phuThuoc: [loai, nguon, trang, soDong],
  });
  const dong = kq.duLieu?.items ?? [];
  const batDau = (trang - 1) * soDong;
  const c = t.cot;

  const cot: CotBang<DongKho>[] = [
    { khoa: 'stt', nhan: c.stt, ve: (d) => batDau + dong.indexOf(d) + 1 },
    { khoa: 'ketNoi', nhan: c.ketNoi, ve: (d) => <OKetNoi d={d} /> },
    { khoa: 'phuongThuc', nhan: c.phuongThuc, ve: (d) => <OPhuongThuc d={d} /> },
    { khoa: 'diaBan', nhan: c.diaBan, ve: (d) => <ODiaBan d={d} /> },
    {
      khoa: 'tenThietBi',
      nhan: c.tenThietBi,
      ve: (d) => (
        <>
          {d.tenThietBi ?? '—'}
          <span className="tb-ma-chip">{d.cumLoaId}</span>
        </>
      ),
    },
    {
      khoa: 'amLuong',
      nhan: c.amLuong,
      canPhai: true,
      ve: (d) => (d.amLuong === null ? '—' : <strong>{d.amLuong}</strong>),
    },
    { khoa: 'capNhat', nhan: c.capNhat, ve: (d) => thoiDiemNgan(d.lanCuoiThay) },
    {
      khoa: 'maNhaCungCap',
      nhan: c.maNhaCungCap,
      ve: (d) => (
        <>
          {d.maNhaCungCap}
          {d.tenNhaCungCap ? <span className="bang__dong-phu">{d.tenNhaCungCap}</span> : null}
        </>
      ),
    },
    {
      khoa: 'nhietDo',
      nhan: c.nhietDo,
      canPhai: true,
      ve: (d) => (d.nhietDo === null ? '—' : `${d.nhietDo} °C`),
    },
    { khoa: 'phienBan', nhan: c.phienBan, ve: (d) => d.phienBanUngDung ?? '—' },
  ];

  const dangKy = async (): Promise<void> => {
    datDangGui(true);
    datLoi(null);
    try {
      const r = await api.duaVaoKho([...daChon]);
      onDaDuaVaoKho(r.soDong);
    } catch (e) {
      datLoi(e instanceof LoiApi || e instanceof Error ? e.message : String(e));
    } finally {
      datDangGui(false);
    }
  };

  return (
    <div className="man-che" role="presentation">
      <div className="hop-thoai kho-hop" role="dialog" aria-modal="true" aria-labelledby={idTieuDe}>
        <div className="kho-hop__dau">
          <h2 id={idTieuDe}>{t.hopTieuDe}</h2>
          <button type="button" className="kho-hop__dong" aria-label={t.huy} onClick={onDong}>
            ×
          </button>
        </div>
        <p className="kho-hop__mo-ta">{t.hopMoTa}</p>
        <div className="kho-hop__loc">
          <ChonDonViHanhChinh
            id="kho-hop-don-vi"
            anNhan
            giaTri={nguon}
            cay={cay}
            onDoi={(v) => {
              datNguon(v);
              datTrang(1);
              datDaChon(new Set());
            }}
          />
          <span className="kho-hop__lien-ket">
            <Link className="nut" to="/thiet-bi/moi">
              {t.khaiBaoThuCong}
            </Link>
            {loai === 'cum_thu_phat_thanh' ? (
              <Link className="nut nut--chinh" to="/thiet-bi/nhap-khau">
                {t.nhapExcel}
              </Link>
            ) : null}
          </span>
        </div>
        <BangDuLieu<DongKho>
          nhan={t.hopTieuDe}
          cot={cot}
          dong={dong}
          khoaDong={(d) => d.id}
          nhanDong={(d) => `${d.tenThietBi ?? d.cumLoaId} (${d.cumLoaId})`}
          trangThai={kq.trangThai}
          loi={kq.loi}
          taiLau={kq.taiLau}
          onThuLai={kq.taiLai}
          tong={kq.duLieu?.total ?? 0}
          trang={trang}
          soDongMoiTrang={soDong}
          onDoiTrang={(so) => datTrang(so)}
          onDoiSoDong={(s) => {
            datSoDong(s);
            datTrang(1);
          }}
          daChon={daChon}
          onDoiChon={datDaChon}
          dangLoc={nguon !== ''}
          onXoaLoc={() => datNguon('')}
          moTaRong={t.rongPhatHien}
        />
        {loi ? (
          <p className="ncc-loi" role="alert">
            {loi}
          </p>
        ) : null}
        <div className="man-hinh__hanh-dong kho-hop__chan">
          <button type="button" className="nut" onClick={onDong}>
            {t.huy}
          </button>
          <button
            type="button"
            className="nut nut--chinh"
            data-hanh-dong="dua-vao-kho"
            disabled={daChon.size === 0 || dangGui}
            onClick={() => void dangKy()}
          >
            {t.dangKy}
            {daChon.size > 0 ? ` (${daChon.size})` : ''}
          </button>
        </div>
        <span className="chi-cho-doc-man-hinh" aria-live="polite">
          {daChon.size > 0 ? dienVao('{so}', { so: daChon.size }) : ''}
        </span>
      </div>
    </div>
  );
}
